import { newsPosts, galleryImages } from '@/data/chkz';

// ---- Types ----

export interface NewsPost {
  id: string;
  image: string;
  date: string;
  title: string;
  description: string;
}

export interface GalleryItem {
  id: string;
  src: string;
  alt: string;
  caption: string;
}

export interface ActivityCard {
  id: string;
  title: string;
  description: string;
  icon: string;
  image: string;
}

export interface SiteTexts {
  [key: string]: string;
}

/** Named image slots the admin can swap via upload in the panel. */
export type ImageSlot = 'logo' | 'heroBg' | 'lakeBg' | 'aboutHero';
export type ImageMap = Partial<Record<ImageSlot, string>>;

/**
 * Default values for the named image slots. Used as a safe fallback (and as
 * the initial state) before/without a backend, and merged under whatever the
 * admin has published in site-data.images.
 */
export const DEFAULT_IMAGE_SLOTS: ImageMap = {
  logo: '/images/logo.png',
  heroBg: '/images/1.webp',
  aboutHero: '/images/3.jpg',
  lakeBg: '/images/2.webp',
};

/**
 * Default activity cards (with their editable images). Mirrors the backend's
 * DEFAULT_ACTIVITIES so a fresh install renders sane cards even before the
 * admin publishes anything.
 */
export const DEFAULT_ACTIVITIES: ActivityCard[] = [
  {
    id: 'act-sailing',
    title: 'Żeglarstwo na żaglach',
    description:
      'Treningi na Jeziorze Miejskim w Chodzieży. Żeglarstwo rekreacyjne i regatowe na żaglówkach dla każdego poziomu zaawansowania.',
    icon: 'Sailboat',
    image: '/images/1_1200x1200.webp',
  },
  {
    id: 'act-regattas',
    title: 'Regaty i rywalizacja',
    description:
      'Uczest. w regatach i zawodach żeglarskich w regionie. Rywalizujemy z innymi klasami, dbając o fair play i sportową atmosferę.',
    icon: 'Trophy',
    image: '/images/2_1280x720.webp',
  },
  {
    id: 'act-community',
    title: 'Społeczność i integracja',
    description:
      'Budujemy żeglarską społeczność — pikniki, wianki, wspólne wyprawy i integracja młodych żeglarzy z rodzinami.',
    icon: 'Users',
    image: '/images/2_900x900.webp',
  },
  {
    id: 'act-wind',
    title: 'Treningi z wiatrem',
    description:
      'Wykorzystujemy każdy wiatr do treningu. Zajęcia na wodzie uczą pracy z żaglem, równowagi i współpracy w załodze.',
    icon: 'Wind',
    image: '/images/1_900x900.webp',
  },
];

// Aggregate shape of the shared SiteData object that every visitor reads from
// the DS-style backend via /api/site-data (opcja 3: live API).
export interface SiteData {
  version: number;
  texts: SiteTexts;
  posts: NewsPost[];
  gallery: GalleryItem[];
  images: ImageMap;
  activities: ActivityCard[];
}

// ---- Storage keys ----

const POSTS_KEY = 'chkz_posts_v1';
const TEXTS_KEY = 'chkz_site_texts_v1';
const SESSION_KEY = 'chkz_admin_session';

// ---- Default posts (seeded from the scraped Instagram news) ----

function seedPosts(): NewsPost[] {
  // newsPosts are stored newest-first; seed the 6 newest as the default feed.
  return newsPosts.slice(0, 6).map((p) => ({
    id: p.id,
    image: p.image,
    date: p.dateFull,
    title: p.caption.split(/[.!?\n]/)[0].slice(0, 70) || 'Nowy post',
    description: p.caption,
  }));
}

export function getPosts(): NewsPost[] {
  try {
    const raw = localStorage.getItem(POSTS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch {
    /* ignore */
  }
  const seeded = seedPosts();
  try {
    localStorage.setItem(POSTS_KEY, JSON.stringify(seeded));
  } catch {
    /* ignore */
  }
  return seeded;
}

export function savePosts(posts: NewsPost[]): void {
  localStorage.setItem(POSTS_KEY, JSON.stringify(posts));
}

export function addPost(post: Omit<NewsPost, 'id'>): NewsPost {
  const posts = getPosts();
  const created: NewsPost = {
    ...post,
    id: 'post_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
  };
  savePosts([created, ...posts]);
  return created;
}

export function updatePost(id: string, patch: Partial<NewsPost>): void {
  const posts = getPosts();
  savePosts(posts.map((p) => (p.id === id ? { ...p, ...patch } : p)));
}

export function deletePost(id: string): void {
  const posts = getPosts();
  savePosts(posts.filter((p) => p.id !== id));
}

// ---- Gallery images ----

const GALLERY_KEY = 'chkz_gallery_v1';

function seedGallery(): GalleryItem[] {
  return galleryImages.slice(0, 10);
}

export function getGallery(): GalleryItem[] {
  try {
    const raw = localStorage.getItem(GALLERY_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch {
    /* ignore */
  }
  const seeded = seedGallery();
  try {
    localStorage.setItem(GALLERY_KEY, JSON.stringify(seeded));
  } catch {
    /* ignore */
  }
  return seeded;
}

export function saveGallery(gallery: GalleryItem[]): void {
  localStorage.setItem(GALLERY_KEY, JSON.stringify(gallery));
}

export function addGalleryImage(image: Omit<GalleryItem, 'id'>): GalleryItem {
  const gallery = getGallery();
  const created: GalleryItem = { ...image, id: 'gal_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6) };
  saveGallery([created, ...gallery]);
  return created;
}

export function updateGalleryImage(id: string, patch: Partial<GalleryItem>): void {
  const gallery = getGallery();
  saveGallery(gallery.map((p) => (p.id === id ? { ...p, ...patch } : p)));
}

export function deleteGalleryImage(id: string): void {
  const gallery = getGallery();
  saveGallery(gallery.filter((p) => p.id !== id));
}

// ---- Site texts ----

export function getSiteTexts(): SiteTexts {
  try {
    const raw = localStorage.getItem(TEXTS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') return parsed;
    }
  } catch {
    /* ignore */
  }
  return {};
}

export function saveSiteTexts(texts: SiteTexts): void {
  localStorage.setItem(TEXTS_KEY, JSON.stringify(texts));
}

// ---- Auth (DS-style: backend issues a bearer token) ----

const ADMIN_LOGIN = 'lok@admin';
const ADMIN_PASSWORD = 'Lok123!';

export function getAuthToken(): string | null {
  try {
    return localStorage.getItem(AUTH_TOKEN_KEY);
  } catch {
    return null;
  }
}

function setAuthToken(token: string): void {
  try {
    localStorage.setItem(AUTH_TOKEN_KEY, token);
  } catch {
    /* ignore */
  }
}

/**
 * Logs in against the backend (POST /api/auth/login). On success stores the
 * bearer token locally and returns true. If the backend is unreachable (network
 * error) OR returns 404/500 (e.g. static preview without the API), it falls
 * back to a local credential check so the admin panel still works offline.
 * Only a real 401 (wrong credentials at the backend) returns an error message.
 */
export async function login(
  email: string,
  password: string
): Promise<boolean | string> {
  // Primary path: authenticate with the backend.
  try {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    if (res.ok) {
      const data = (await res.json()) as { token?: string };
      if (data.token) {
        setAuthToken(data.token);
        return true;
      }
    }

    // 401 = wrong credentials at the backend → real error.
    if (res.status === 401) {
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      return data.error || 'Nieprawidłowy login lub hasło';
    }
    // 404 / 500 / etc. → backend not serving the API → use local fallback.
  } catch {
    /* network error (backend down) → local fallback */
  }

  // Offline / no-API fallback: same credentials, but no token (publish will
  // fail until the backend is back; local edits still work via localStorage).
  if (email.trim().toLowerCase() === ADMIN_LOGIN && password === ADMIN_PASSWORD) {
    sessionStorage.setItem(SESSION_KEY, '1');
    return true;
  }
  return 'Nieprawidłowy login lub hasło';
}

export function logout(): void {
  const token = getAuthToken();
  if (token) {
    fetch('/api/auth/logout', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => {});
  }
  try {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    /* ignore */
  }
}

export function isAuthed(): boolean {
  try {
    return Boolean(getAuthToken()) || sessionStorage.getItem(SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

// ---- Shared site data via the DS-style backend (opcja 3: live API) ----
// Every visitor's loadSiteData() reads /api/site-data, and an authenticated
// admin publishes edits with PUT /api/site-data. This makes an admin edit
// visible to EVERY visitor immediately — no redeploy needed.

const SITE_DATA_URL = '/api/site-data';
export const SITE_DATA_VERSION = 1;
const AUTH_TOKEN_KEY = 'chkz_admin_token';

/**
 * Loads the shared site-data from the backend. Every visitor reads this on
 * load, so a publish from the admin panel propagates to everyone instantly.
 * Falls back to local overrides (localStorage) merged over the server data,
 * then to seeded defaults — so the site always renders something sane even
 * if the backend is unreachable.
 */
export async function loadSiteData(): Promise<SiteData> {
  let file: Partial<SiteData> = {};
  try {
    // cache-bust so a fresh publish is picked up immediately
    const res = await fetch(`${SITE_DATA_URL}?t=${Date.now()}`, { cache: 'no-store' });
    if (res.ok) file = (await res.json()) as Partial<SiteData>;
  } catch {
    /* backend offline — fall through to local + defaults */
  }

  const localPosts = readLocal<NewsPost[]>(POSTS_KEY, null);
  const localGallery = readLocal<GalleryItem[]>(GALLERY_KEY, null);
  const localTexts = readLocal<SiteTexts>(TEXTS_KEY, null);

  // The backend (file) is the shared "database" every visitor should see, so it
  // takes precedence. localStorage is only a fallback when the backend is
  // unreachable (this also avoids the seeded-default localStorage — written by
  // getGallery()/getPosts() during component mount — overriding published data).
  return {
    version: file.version ?? SITE_DATA_VERSION,
    texts: { ...(localTexts ?? {}), ...(file.texts ?? {}) },
    posts: file.posts ?? localPosts ?? seedPosts(),
    gallery: file.gallery ?? localGallery ?? seedGallery(),
    images: { ...DEFAULT_IMAGE_SLOTS, ...(file.images ?? {}) } as ImageMap,
    activities: (file.activities && file.activities.length ? file.activities : DEFAULT_ACTIVITIES) as ActivityCard[],
  };
}

/**
 * Loads just the named image slots (logo/hero/lake/about). Every visitor reads
 * this; after an admin publishes a new upload, a fresh call picks it up.
 */
export async function loadImages(): Promise<ImageMap> {
  try {
    const res = await fetch(`${SITE_DATA_URL}?t=${Date.now()}`, { cache: 'no-store' });
    if (res.ok) {
      const data = (await res.json()) as Partial<SiteData>;
      return { ...DEFAULT_IMAGE_SLOTS, ...(data.images ?? {}) } as ImageMap;
    }
  } catch {
    /* offline */
  }
  return { ...DEFAULT_IMAGE_SLOTS } as ImageMap;
}

/** Loads the activity cards (with editable images) from the backend. */
export async function loadActivities(): Promise<ActivityCard[]> {
  try {
    const res = await fetch('/api/activities', { cache: 'no-store' });
    if (res.ok) {
      const data = (await res.json()) as { activities?: ActivityCard[] };
      if (data.activities && data.activities.length) return data.activities;
    }
  } catch {
    /* offline */
  }
  return DEFAULT_ACTIVITIES as ActivityCard[];
}

/**
 * Notifies all live consumers (sections, ImageUploadField, useImages/useActivities
 * hooks) that a fresh publish happened, so they re-fetch the shared site data.
 * Called by the admin panel right after a successful publish.
 */
export function notifyPublished(): void {
  try {
    window.dispatchEvent(new Event('chkz:published'));
  } catch {
    /* ignore */
  }
}

/**
 * Publishes the full SiteData to the backend. Called by the admin panel's
 * "Zapisz i opublikuj" button. Requires the auth bearer token obtained at
 * login. Returns true on success.
 */
export async function saveSiteDataToServer(
  data: SiteData,
  token?: string
): Promise<boolean> {
  const authToken = token ?? getAuthToken();
  if (!authToken) return false;
  try {
    const res = await fetch(SITE_DATA_URL, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify(data),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * Assembles the deployable SiteData object from the current admin state.
 * Used by the "Export" button to produce the file that gets deployed.
 */
export function buildSiteData(opts: {
  texts: SiteTexts;
  posts: NewsPost[];
  gallery: GalleryItem[];
  images: ImageMap;
  activities: ActivityCard[];
}): SiteData {
  return {
    version: SITE_DATA_VERSION,
    texts: opts.texts,
    posts: opts.posts,
    gallery: opts.gallery,
    images: opts.images,
    activities: opts.activities,
  } as SiteData;
}

/**
 * Triggers a browser download of site-data.json. The admin then drops this
 * file into /public and redeploys — which is what makes the change visible
 * to every visitor (no backend in opcja 2).
 */
export function exportSiteDataFile(data: SiteData): void {
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'site-data.json';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function readLocal<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed !== null && parsed !== undefined) return parsed as T;
    }
  } catch {
    /* ignore */
  }
  return fallback;
}
